import React from 'react';
import { UserDetailsResponse } from '../../types/user';
import { X, User, Clock, Activity, Calendar, Monitor } from 'lucide-react';

interface UserDetailsModalProps {
  userDetails: UserDetailsResponse | null;
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  userDetails,
  isOpen,
  onClose,
  loading = false
}) => {
  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      manager: { label: 'Manager', color: 'bg-purple-100 text-purple-800' },
      executive1: { label: 'Executive 1', color: 'bg-blue-100 text-blue-800' },
      executive2: { label: 'Executive 2', color: 'bg-green-100 text-green-800' },
      worker: { label: 'Worker', color: 'bg-gray-100 text-gray-800' }
    };
    
    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig.worker;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!loading && userDetails && (
          <div className="space-y-6">
            {/* User Info Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-16 w-16 bg-gray-300 rounded-full flex items-center justify-center relative">
                    <span className="text-xl font-medium text-gray-700">
                      {userDetails.user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 
                       userDetails.user.username.charAt(0).toUpperCase()}
                    </span>
                    <div className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white ${
                      userDetails.user.is_active ? 'bg-green-500' : 'bg-red-500'
                    }`}></div>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">
                      {userDetails.user.full_name || userDetails.user.username}
                    </h4>
                    <p className="text-gray-600">@{userDetails.user.username}</p>
                    <p className="text-sm text-gray-500">{userDetails.user.email}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      {getRoleBadge(userDetails.user.role)}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        userDetails.user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {userDetails.user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>Created: {formatDate(userDetails.user.created_at)}</span>
                  </div>
                  {userDetails.user.last_login_at && (
                    <div className="flex items-center mt-1">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>Last login: {formatDate(userDetails.user.last_login_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <Activity className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Total Sessions</p>
                    <p className="text-2xl font-bold text-blue-600">{userDetails.login_stats.total_sessions}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <Clock className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Last 30 Days</p>
                    <p className="text-2xl font-bold text-green-600">{userDetails.login_stats.last_30_days}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                    <Monitor className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Avg. Session</p>
                    <p className="text-2xl font-bold text-purple-600">{userDetails.login_stats.average_session_duration}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Sessions */}
            <div className="bg-white border rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h5 className="text-lg font-medium text-gray-900">Recent Sessions</h5>
              </div>
              <div className="overflow-x-auto">
                {userDetails.recent_sessions.length === 0 ? (
                  <div className="px-6 py-8 text-center text-gray-500">
                    No recent sessions found
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Login Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User Agent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duration
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userDetails.recent_sessions.map((session, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(session.login_time)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {session.login_ip}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                            {session.user_agent}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {session.session_duration}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && !userDetails && (
          <div className="text-center py-12">
            <p className="text-gray-500">Failed to load user details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailsModal;